import React from 'react';
import QuoteBanner, { Amount } from 'src/components/QuoteBanner';
import { Badge, Link } from 'straightline-ui';
import {
  Col,
  Icon,
  ListGroup,
  ListGroupItem,
  Row,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  UncontrolledTooltip
} from 'straightline-ui';
import { formatMoney, formatNumber } from 'straightline-utils/accounting';
import { isAccepted, isExpired, truckType } from 'straightline-utils/constants';
import { formatDate, formatDistanceToNowStrict } from 'straightline-utils/time';

export const ShipmentQuotesTable = ({ data = [], isLoading = false }) => {
  if (isLoading) {
    return <ShipmentQuotesTableLoader />;
  }

  return (
    <div className="table-responsive">
      <Table className="table table-nowrap card-table table-hover">
        <TableHead>
          <TableRow>
            <TableHeader className="py-3">Status</TableHeader>
            <TableHeader className="py-3">Price</TableHeader>
            <TableHeader className="py-3">ID</TableHeader>
            <TableHeader className="py-3">Pickup</TableHeader>
            <TableHeader className="py-3">Delivery</TableHeader>
            <TableHeader className="py-3">Details</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody className="list">
          {data.map((quote) => {
            return <ShipmentQuotesTableRow quote={quote} key={quote.id} />;
          })}
        </TableBody>
      </Table>
    </div>
  );
};

function getOffer(quote) {
  if (!quote.offers || !quote.offers.length) {
    return undefined;
  }
  if (quote.offers.length === 1) {
    return quote.offers[0];
  }
  const accepted = quote.offers.find((offer) => offer.accepted_at);
  if (accepted) {
    return accepted;
  }
  return quote.offers[0];
}

export const QuoteUnits = ({ quote }) => {
  const units = quote.line_items.reduce((units, item) => {
    return units + item.units;
  }, 0);
  return (
    <div>
      <div className="d-flex align-items-center">
        <Icon type="compass" className="text-secondary mr-1" />
        <span className="mr-3">{quote.mode}</span>
        <Icon type="truck" className="text-secondary mr-1" />
        <span className="mr-3">{truckType(quote.truck_type).label}</span>
        <Icon type="list" className="text-secondary mr-1" />
        <span className="mr-3">
          {formatNumber(quote.accessorial_codes?.length || 0)} accs
        </span>
      </div>
      <div className="d-flex align-items-center">
        <Icon type="inbox" className="text-secondary mr-1" />
        <span className="mr-3">{formatNumber(units)} units</span>
        <Icon type="anchor" className="text-secondary mr-1" />
        <span className="mr-3">{formatNumber(quote.load_weight)} lbs</span>
        <Icon type="map" className="text-secondary mr-1" />
        <span className="mr-3">{formatNumber(quote.travel.miles)} mi</span>
      </div>
    </div>
  );
};

function truncateText(text) {
  if (!text) {
    return '';
  }

  if (text.length <= 20) {
    return text;
  }

  return text.substr(0, 20) + '...';
}

const ShipmentQuotesTableRow = ({ quote }) => {
  const expired = isExpired(quote);
  const accepted = isAccepted(quote);
  const offer = getOffer(quote);

  if (accepted) {
    return <Accepted quote={quote} key={quote.id} />;
  }

  if (expired) {
    return <Expired quote={quote} key={quote.id} />;
  }

  return (
    <React.Fragment>
      <TableRow key={quote.id} to={`/quotes/${quote.id}`}>
        <TableData>
          <Badge color="soft-primary" style={{ padding: 8.5 }}>
            Quoted
          </Badge>
        </TableData>
        <TableData>
          {offer && (
            <div className="d-inline-block">
              {offer.accepted_at ? (
                <div className="text-primary">
                  Your offer of{' '}
                  <span className="font-weight-bold">
                    {formatMoney(offer.amount)}
                  </span>
                  <br />
                  has been accepted!
                </div>
              ) : (
                <div className="text-secondary">
                  Your offer of{' '}
                  <span className="font-weight-bold">
                    {formatMoney(offer.amount)}
                  </span>
                  <br />
                  is being reviewed
                </div>
              )}
            </div>
          )}
          {!offer && (
            <div>
              <div className="font-weight-bold text-primary">
                <Amount
                  min={quote.rates_range.min}
                  max={quote.rates_range.max}
                />
              </div>
              <div className="text-primary">
                expires {formatDistanceToNowStrict(quote.expired_at)}
              </div>
            </div>
          )}
        </TableData>
        <TableData>
          <div>
            <span className="text-muted">SL#: </span>
            {quote.id}
          </div>
          <div>
            <span className="text-muted">REF#: </span>
            {truncateText(quote.reference_number) || (
              <span className="text-muted">NA</span>
            )}
          </div>
        </TableData>
        <TableData>
          <div>
            {`${quote.pickup_address.city}, ${quote.pickup_address.state}`}
            {quote.pickup_address.name && (
              <span id={`pickup-name-${quote.id}`} className="ml-1">
                <Icon type="info" className="text-secondary" />
                <UncontrolledTooltip target={`pickup-name-${quote.id}`}>
                  {quote.pickup_address.name}
                </UncontrolledTooltip>
              </span>
            )}
          </div>
          <div>{formatDate(quote.pickup_date, 'LLL do, yyyy')}</div>
        </TableData>
        <TableData className="px-3 px-xl-4" style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', left: -12, top: 21 }}>
            <Icon type="arrow-right text-success" style={{ fontSize: 25 }} />
          </div>
          <div>
            {`${quote.delivery_address.city}, ${quote.delivery_address.state}`}
            {quote.delivery_address.name && (
              <span id={`delivery-name-${quote.id}`} className="ml-1">
                <Icon type="info" className="text-secondary" />
                <UncontrolledTooltip target={`delivery-name-${quote.id}`}>
                  {quote.delivery_address.name}
                </UncontrolledTooltip>
              </span>
            )}
          </div>
          <div>{formatDate(quote.due_date, 'LLL do, yyyy')}</div>
        </TableData>
        <TableData>
          <QuoteUnits quote={quote} />
        </TableData>
      </TableRow>
    </React.Fragment>
  );
};

const Accepted = ({ quote }) => {
  const rate = quote.rates.find((rate) => {
    return rate.id === quote.rate_id;
  });
  return (
    <TableRow to={`/quotes/${quote.id}`}>
      <TableData>
        <Badge color="soft-success" style={{ padding: 8.5 }}>
          Accepted
        </Badge>
      </TableData>
      <TableData>
        <div className="font-weight-bold text-success">
          <Amount min={quote.rates_range.min} max={quote.rates_range.max} />
        </div>
        <div className="text-success">
          Accepted for{' '}
          {rate && formatMoney(rate.price.shipper_price, { precision: 0 })}
        </div>
      </TableData>
      <TableData>
        <div>
          <span className="text-muted">SL#: </span>
          {quote.id}
        </div>
        <div>
          <span className="text-muted">REF#: </span>
          {truncateText(quote.reference_number) || (
            <span className="text-muted">NA</span>
          )}
        </div>
      </TableData>
      <TableData>
        <div>
          {`${quote.pickup_address.city}, ${quote.pickup_address.state}`}
          {quote.pickup_address.name && (
            <span id={`pickup-name-${quote.id}`} className="ml-1">
              <Icon type="info" className="text-secondary" />
              <UncontrolledTooltip target={`pickup-name-${quote.id}`}>
                {quote.pickup_address.name}
              </UncontrolledTooltip>
            </span>
          )}
        </div>
        <div>{formatDate(quote.pickup_date, 'LLL do, yyyy')}</div>
      </TableData>
      <TableData className="px-3 px-xl-4" style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: -12, top: 21 }}>
          <Icon type="arrow-right text-success" style={{ fontSize: 25 }} />
        </div>
        <div>
          {`${quote.delivery_address.city}, ${quote.delivery_address.state}`}
          {quote.delivery_address.name && (
            <span id={`delivery-name-${quote.id}`} className="ml-1">
              <Icon type="info" className="text-secondary" />
              <UncontrolledTooltip target={`delivery-name-${quote.id}`}>
                {quote.delivery_address.name}
              </UncontrolledTooltip>
            </span>
          )}
        </div>
        <div>{formatDate(quote.due_date, 'LLL do, yyyy')}</div>
      </TableData>
      <TableData>
        <QuoteUnits quote={quote} />
      </TableData>
    </TableRow>
  );
};

const Expired = ({ quote }) => {
  return (
    <TableRow to={`/quotes/${quote.id}`}>
      <TableData>
        <Badge color="soft-danger" style={{ padding: 8.5 }}>
          Expired
        </Badge>
      </TableData>
      <TableData>
        <div className="font-weight-bold text-danger">
          <Amount del min={quote.rates_range.min} max={quote.rates_range.max} />
        </div>
        <div className="text-danger">
          Expired on {formatDate(quote.expired_at)}
        </div>
      </TableData>
      <TableData>
        <div>
          <span className="text-muted">SL#: </span>
          {quote.id}
        </div>
        <div>
          <span className="text-muted">REF#: </span>
          {truncateText(quote.reference_number) || (
            <span className="text-muted">NA</span>
          )}
        </div>
      </TableData>
      <TableData>
        <div>
          {`${quote.pickup_address.city}, ${quote.pickup_address.state}`}
          {quote.pickup_address.name && (
            <span id={`pickup-name-${quote.id}`} className="ml-1">
              <Icon type="info" className="text-secondary" />
              <UncontrolledTooltip target={`pickup-name-${quote.id}`}>
                {quote.pickup_address.name}
              </UncontrolledTooltip>
            </span>
          )}
        </div>
        <div>{formatDate(quote.pickup_date, 'LLL do, yyyy')}</div>
      </TableData>
      <TableData className="px-3 px-xl-4" style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: -12, top: 21 }}>
          <Icon type="arrow-right text-success" style={{ fontSize: 25 }} />
        </div>
        <div>
          {`${quote.delivery_address.city}, ${quote.delivery_address.state}`}
          {quote.delivery_address.name && (
            <span id={`delivery-name-${quote.id}`} className="ml-1">
              <Icon type="info" className="text-secondary" />
              <UncontrolledTooltip target={`delivery-name-${quote.id}`}>
                {quote.delivery_address.name}
              </UncontrolledTooltip>
            </span>
          )}
        </div>
        <div>{formatDate(quote.due_date, 'LLL do, yyyy')}</div>
      </TableData>
      <TableData>
        <QuoteUnits quote={quote} />
      </TableData>
    </TableRow>
  );
};

export const ShipmentQuotesList = ({ data = [], isLoading = false }) => {
  if (isLoading) {
    return <ShipmentQuotesListLoader />;
  }
  return (
    <ListGroup className="list-group-flush">
      {data.map((quote) => {
        return (
          <ListGroupItem
            key={quote.id}
            tag={Link}
            to={`/quotes/${quote.id}`}
            className="list-group-item-action px-3"
          >
            <ShipmentQuoteMobileView quote={quote} />
          </ListGroupItem>
        );
      })}
    </ListGroup>
  );
};

export const ShipmentQuoteMobileView = ({ quote }) => {
  const offer = getOffer(quote);
  const expired = isExpired(quote);
  const accepted = isAccepted(quote);

  return (
    <div>
      <QuoteBanner quote={quote} />
      <ListGroup className="list-group list-group-flush list-group-activity">
        <ListGroupItem className="py-3">
          <Row>
            <Col xs="auto">
              <div className="avatar avatar-sm">
                <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                  <Icon type="clipboard" />
                </div>
              </div>
            </Col>
            <Col className="ml-n2">
              <div className="font-weight-bold">
                {(() => {
                  if (accepted) {
                    return (
                      <div className="text-success">
                        Accepted on{' '}
                        {formatDate(quote.accepted_at, 'LLL d, yyyy')}.
                      </div>
                    );
                  }

                  if (expired) {
                    return (
                      <div className="text-danger">
                        Expired on {formatDate(quote.expired_at, 'LLL d, yyyy')}
                        .
                      </div>
                    );
                  }

                  if (!offer) {
                    return (
                      <div className="text-muted">
                        You may make one counteroffer to this price.
                      </div>
                    );
                  }

                  return (
                    <div
                      className={
                        offer.accepted_at ? 'text-primary' : 'text-secondary'
                      }
                    >
                      Your offer of{' '}
                      <span className="font-weight-bold">
                        {formatMoney(offer.amount)}
                      </span>{' '}
                      <br className="d-none d-sm-inline" />
                      {offer.accepted_at
                        ? 'has been accepted!'
                        : 'is being reviewed.'}
                    </div>
                  );
                })()}
              </div>
              {!expired && !accepted && (
                <div>Expires {formatDistanceToNowStrict(quote.expired_at)}</div>
              )}
              <div>SL#: {quote.id}</div>
              <div>
                REF#:{' '}
                {quote.reference_number || (
                  <span className="text-muted">NA</span>
                )}
              </div>
            </Col>
          </Row>
        </ListGroupItem>
        <ListGroupItem className="py-3">
          <Row>
            <Col xs="auto">
              <div className="avatar avatar-sm">
                <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                  <Icon type="truck" />
                </div>
              </div>
            </Col>
            <Col className="ml-n2">
              {quote.pickup_address.name && (
                <div className="font-weight-bold">
                  {quote.pickup_address.name}
                </div>
              )}
              <div
                className={quote.pickup_address.name ? '' : 'font-weight-bold'}
              >
                {`${quote.pickup_address.city}, ${quote.pickup_address.state}`}
              </div>
              <div>{formatDate(quote.pickup_date, 'LLL d, yyyy')}</div>
            </Col>
          </Row>
        </ListGroupItem>
        <ListGroupItem className="pt-3 pb-0">
          <Row>
            <Col xs="auto">
              <div className="avatar avatar-sm">
                <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                  <Icon type="map-pin" />
                </div>
              </div>
            </Col>
            <Col className="ml-n2">
              {quote.delivery_address.name && (
                <div className="font-weight-bold">
                  {quote.delivery_address.name}
                </div>
              )}
              <div
                className={
                  quote.delivery_address.name ? '' : 'font-weight-bold'
                }
              >
                {`${quote.delivery_address.city}, ${quote.delivery_address.state}`}
              </div>
              <div>{formatDate(quote.due_date, 'LLL d, yyyy')}</div>
            </Col>
          </Row>
        </ListGroupItem>
      </ListGroup>
    </div>
  );
};

export const ShipmentQuotesTableLoader = ({ length = 100 }) => {
  const loaderLength = new Array(length).fill(null);
  return (
    <div className="table-responsive">
      <Table className="table table-nowrap card-table table-hover">
        <TableHead>
          <TableRow>
            <TableHeader className="py-3">Status</TableHeader>
            <TableHeader className="py-3">Price</TableHeader>
            <TableHeader className="py-3">ID</TableHeader>
            <TableHeader className="py-3">Pickup</TableHeader>
            <TableHeader className="py-3" style={{ paddingLeft: 38 }}>
              Delivery
            </TableHeader>
            <TableHeader className="py-3" colSpan="2">
              Status
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody className="list">
          {loaderLength.map((item) => {
            return (
              <TableRow key={item}>
                <TableData>
                  <div
                    className="bg-light rounded mb-2"
                    style={{ height: 18, width: 85 }}
                  />
                  <div
                    className="bg-light rounded"
                    style={{ height: 18, width: 40 }}
                  />
                </TableData>
                <TableData className="pr-0">
                  <div
                    className="bg-light rounded mb-2"
                    style={{ height: 18, width: 78 }}
                  />
                  <div
                    className="bg-light rounded"
                    style={{ height: 18, width: 65 }}
                  />
                </TableData>
                <TableData className="pr-0">
                  <div
                    className="bg-light rounded mb-2"
                    style={{ height: 18, width: 78 }}
                  />
                  <div
                    className="bg-light rounded"
                    style={{ height: 18, width: 65 }}
                  />
                </TableData>
                <TableData className="pl-0">
                  <div className="d-inline-block mx-2">
                    <Icon
                      type="arrow-right"
                      className="text-light"
                      style={{ fontSize: 25 }}
                    />
                  </div>
                  <div className="d-inline-block">
                    <div
                      className="bg-light rounded mb-2"
                      style={{ height: 18, width: 78 }}
                    />
                    <div
                      className="bg-light rounded"
                      style={{ height: 18, width: 65 }}
                    />
                  </div>
                </TableData>
                <TableData>
                  <div
                    className="bg-light rounded mb-2"
                    style={{ height: 18, width: 100 }}
                  />
                  <div
                    className="bg-light rounded"
                    style={{ height: 18, width: 65 }}
                  />
                </TableData>
                <TableData>
                  <div
                    className="bg-light rounded d-inline-block mr-2"
                    style={{ height: 41, width: 142 }}
                  />
                  <div
                    className="bg-light rounded d-inline-block mr-2"
                    style={{ height: 41, width: 142 }}
                  />
                </TableData>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export const ShipmentQuotesListLoader = ({ length = 10 }) => {
  const loaderLength = new Array(length).fill(null);
  return (
    <ListGroup className="list-group-flush mb-0">
      {loaderLength.map((item, index) => {
        return (
          <ListGroupItem key={index} className="px-4">
            <div
            // className="animated flash infinite slower"
            >
              <Row className="justify-content-between mb-3">
                <Col xs="auto">
                  <div
                    className="bg-light rounded"
                    style={{ height: 33, width: 100 }}
                  />
                </Col>
                <Col xs="auto">
                  <div className="text-secondary d-flex align-items-center">
                    <div
                      className="bg-light rounded"
                      style={{ height: 14, width: 40 }}
                    />
                    <Icon type="dot" className="text-muted mx-1" />
                    <div
                      className="bg-light rounded"
                      style={{ height: 14, width: 60 }}
                    />
                  </div>
                  <div className="text-secondary d-flex align-items-center">
                    <div
                      className="bg-light rounded"
                      style={{ height: 14, width: 60 }}
                    />
                    <Icon type="dot" className="text-muted mx-1" />
                    <div
                      className="bg-light rounded"
                      style={{ height: 14, width: 40 }}
                    />
                  </div>
                </Col>
              </Row>
              <ListGroup className="list-group list-group-flush list-group-activity">
                <ListGroupItem className="py-3">
                  <Row>
                    <Col xs="auto">
                      <div className="avatar avatar-sm">
                        <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                          <Icon type="clipboard" />
                        </div>
                      </div>
                    </Col>
                    <Col className="ml-n2">
                      <div
                        className="bg-light rounded mb-1"
                        style={{ height: 14, width: 120 }}
                      />
                      <div
                        className="bg-light rounded mb-1"
                        style={{ height: 14, width: 100 }}
                      />
                      <div
                        className="bg-light rounded mb-1"
                        style={{ height: 14, width: 190 }}
                      />
                      <div
                        className="bg-light rounded mb-1"
                        style={{ height: 14, width: 75 }}
                      />
                    </Col>
                  </Row>
                </ListGroupItem>

                <ListGroupItem className="py-3">
                  <Row>
                    <Col xs="auto">
                      <div className="avatar avatar-sm">
                        <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                          <Icon type="truck" />
                        </div>
                      </div>
                    </Col>
                    <Col className="ml-n2">
                      <div>
                        <div
                          className="bg-light rounded mb-2"
                          style={{ height: 18, width: 160 }}
                        />
                        <div>
                          <div
                            className="bg-light rounded"
                            style={{ height: 16, width: 140 }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </ListGroupItem>
                <ListGroupItem className="pt-3 pb-0">
                  <Row>
                    <Col xs="auto">
                      <div className="avatar avatar-sm">
                        <div className="avatar-title font-size-lg rounded-circle bg-light text-dark">
                          <Icon type="map-pin" />
                        </div>
                      </div>
                    </Col>
                    <Col className="ml-n2">
                      <div>
                        <div
                          className="bg-light rounded mb-2"
                          style={{ height: 18, width: 170 }}
                        />
                        <div
                          className="bg-light rounded"
                          style={{ height: 18, width: 170 }}
                        />
                      </div>
                    </Col>
                  </Row>
                </ListGroupItem>
              </ListGroup>
            </div>
          </ListGroupItem>
        );
      })}
    </ListGroup>
  );
};
